import styled, { css } from 'styled-components';

import { BaseButton } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { Label } from '@r1-frontend/ui-react/components/label';
import AccordionInstance from '@r1-frontend/ui-react/deprecated/Accordion';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';
import { BoxShadow } from '@r1-frontend/ui-react/tokens/boxShadow';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';
import { hex2rgb } from '@r1-frontend/shared/helpers/hex2rgb';

import { ACBreakpoints } from '~/src/components/TariffFilter/styled';

const getGradient = (colors: string[]): string => {
    const [leftColor, rightColor] = colors;
    return `linear-gradient(90deg, ${leftColor} 0%, ${rightColor} 100%)`;
};

export const CardWrapper = styled.div<{ tiled: boolean }>`
    position: relative;
    color: ${COLORS.TextPrimary};
    max-width: 304px;
    height: 468px;
    flex-direction: column;
    flex-wrap: nowrap;
    white-space: pre-wrap;
    border-radius: ${BorderRadius.OuterBlockRadius};
    overflow: hidden;
    text-rendering: geometricPrecision;
    transition: all .3s ease;
    cursor: grab;
    ${({ tiled }) => tiled && css`
        margin: 0 16px 24px;
        cursor: default;

        @media (max-width: ${ACBreakpoints.sDesktop}) {
            margin: 0 8px 24px;
        }

        @media (max-width: ${ACBreakpoints.laptop}) {
            margin: 0 20px 24px;
        }

        @media (max-width: ${ACBreakpoints.tablet}) {
            margin: 0 8px 24px;
        }

        @media (max-width: ${ACBreakpoints.sMobile}) {
            margin: 0 0 24px;
        }
    `};

    &:hover {
        box-shadow: ${BoxShadow.M};
    }

    &:active {
        cursor: grabbing;
    }
`;

export const ContentSection = styled.div<{ bg?: string, bgSize: string, colorText: string }>`
    height: 323px;
    flex-direction: column;
    flex-wrap: nowrap;
    padding: 32px 24px 16px;
    color: ${p => p.colorText ?? COLORS.TextPrimary};
    background: ${p => `url(${p.bg}) 0 0 / ${p.bgSize} repeat-y`};
    background: image-set(${p => `url(${p.bg}) 1x) 0 0 / ${p.bgSize} repeat-y`};
    background-color: ${COLORS.BgSurface};
    border-bottom: 1px solid ${COLORS.BgLight};
    background-size: cover;
`;

export const TariffTitle = styled.h3<{ colors?: Array<string>, withBg: boolean }>`
    display: block;
    ${FONTS.H3};
    background: ${({ colors }) => {
        if (colors?.length) {
            return colors[0] === colors[1]
                ? 'none'
                : getGradient(colors);
        }
    }};
    -webkit-background-clip: text;
    color: ${({ colors }) => {
        if (colors?.length) {
            return colors[0] === colors[1]
                ? colors[0]
                : 'transparent';
        }
        return 'inherit';
    }};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const TariffDescription = styled.p`
    height: 32px;
    ${FONTS.XXS};
    margin-top: 4px;
`;

export const SubsriptionsBlock = styled.div<{ height: number }>`
    width: ${({ height }) => `calc(100% + ${height + 2}px)`};
    height: ${({ height }) => `${height}px`};
    margin: ${({ height }) => `8px -${(height + 2) / 2}px 4px`};
    flex-wrap: nowrap;
`;

export const LeftArrow = styled(BaseButton)<{ isVisible: boolean, height: number }>`
    height: ${({ height }) => `${height}px`};
    width: ${({ isVisible, height }) => isVisible ? `${height}px` : 0};
    padding: ${({ isVisible }) => isVisible ? '8px' : 0};
    margin-right: ${({ isVisible }) => isVisible ? '4px' : 0};
    background-color: ${COLORS.BgSecondary};
    background-image: url('/static/images/icons/left-arrow.svg');
    background-position: center;
    background-repeat: no-repeat;
    border-radius: ${BorderRadius.labelRadius};
    overflow: ${({ isVisible }) => isVisible ? 'visible' : 'hidden'};
    transition: .2s;
    cursor: pointer;

    &:hover {
        background-color: ${COLORS.BgLight};
    }
`;

export const Subscriptions = styled.div<{ withLeftBtn: boolean, withRightBtn: boolean, height: number }>`
    position: relative;
    flex: 1 1 auto;
    margin-left: ${({ withLeftBtn, height }) => withLeftBtn ? 0 : `${height / 2}px`};
    margin-right: ${({ withRightBtn, height }) => withRightBtn ? 0 : `${height / 2}px`};
    flex-wrap: nowrap;
    overflow: hidden;
`;

export const InnerContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: auto;
    height: 100%;
    flex-wrap: nowrap;
    align-items: center;
    transition: all .2s ease;
`;

export const RightArrow = styled.div<{ isVisible: boolean, height: number }>`
    height: ${({ height }) => `${height}px`};
    width: ${({ isVisible, height }) => isVisible ? `${height}px` : 0};
    padding: ${({ isVisible }) => isVisible ? '8px' : 0};
    margin-left: ${({ isVisible }) => isVisible ? '4px' : 0};
    background-color: ${COLORS.BgSecondary};
    background-image: url('/static/images/icons/right-arrow.svg');
    background-position: center;
    background-repeat: no-repeat;
    border-radius: ${BorderRadius.labelRadius};
    overflow: ${({ isVisible }) => isVisible ? 'visible' : 'hidden'};
    transition: .2s;
    cursor: pointer;

    &:hover {
        background-color: ${COLORS.BgLight};
    }
`;

export const ServicesSection = styled.div<{ withSubs: boolean }>`
    height: ${({ withSubs }) => withSubs ? '144px' : '208px'};
    flex-direction: column;
    flex-wrap: nowrap;
`;

const getBg = (bgColor: string, withBg: boolean, opacity?: number) => {
    if (opacity) {
        return `rgba(${hex2rgb(bgColor)}, ${opacity})` || (withBg ? `rgba(${hex2rgb(COLORS.BgMain)}, ${opacity})` : COLORS.BgMain);
    }

    return bgColor || (withBg ? `rgba(${hex2rgb(COLORS.BgMain)}, .2)` : COLORS.BgMain);
};

export const AccServiceBlock = styled(AccordionInstance)<{ bgColor: string, withBg: boolean, isClosed: boolean, open: boolean }>`
    width: 100%;
    border: none;
    color: inherit;
    padding: 0;
    margin-top: 16px;
    background: none;

    & > *:first-child {
        width: calc(100% + 32px);
        margin-left: -16px;
        margin-right: -16px;
        padding: 8px 16px;
        border-radius:  ${BorderRadius.OuterBlockRadius};
        background: ${({ bgColor, withBg }) => getBg(bgColor, withBg)};

        &:hover {
            background: ${({ bgColor, withBg }) => getBg(bgColor, withBg, 0.6)};
        }
    }

    & > *:last-child {
        max-height: unset;
        padding-top: 8px;
        height: ${({ open }) => open ? '48px' : 0};
        transition: all 0.2s ease;
        transition-delay: ${({ isClosed }) => isClosed ? '0s' : '0.1s'};
    }

    & > *:first-child > *:last-child {
        width: 40px;
        height: calc(100% + 16px);
        margin: -8px -8px -8px 4px;
    }

    & > *:last-child > *:first-child {
        height: 0;
        margin: 0;
    }

    & > *:first-child > *:last-child > svg > circle {
        stroke: none;
        fill: none;
    }

    & > *:first-child > *:last-child > svg > path {
        stroke: ${({ withBg }) => withBg ? COLORS.BgMain : COLORS.GRAY_DARK};
    }
`;

export const AccServiceTitle = styled.h4`
    ${FONTS.H5};
`;

export const ServiceBlock = styled.div`
    width: 100%;
    padding: 0;
    margin-top: 16px;
`;

export const ServiceTitle = styled.p<{ withBg: boolean, bgColor: string, colorText: string }>`
    ${FONTS.H5};
    width: calc(100% + 32px);
    margin: 0 -16px 8px;
    padding: 8px 16px;
    border-radius: ${BorderRadius.OuterBlockRadius};
    background: ${({ bgColor, withBg }) => getBg(bgColor, withBg)};
    color: inherit;
`;

export const ServiceIncludesList = styled.ul``;

export const ServiceIncludesItem = styled.li<{ withDetails?: boolean }>`
    position: relative;
    height: 16px;
    ${FONTS.XXS};
    display: flex;
    padding-left: 12px;

    &:before {
        position: absolute;
        content: '';
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 4px;
        border-radius: ${BorderRadius.labelRadius};
        background: ${COLORS.Outline_Dark};
        overflow: hidden;
    }

    &:not(:last-child) {
        margin-bottom: 8px;
    }

    p, div {
        width: ${({ withDetails }) => withDetails ? 'auto' : '244px'};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const ServiceTvDetails = styled.span`
    position: relative;
    ${FONTS.MediumXXS};
    cursor: pointer;
    text-decoration: underline;
    color: inherit;
`;

export const PriceSection = styled.div`
    height: 144px;
    ${FONTS.S};
    padding: 8px 24px 16px;
    display: flex;
    flex-direction: column;
    background: ${COLORS.BgSurface};
`;

export const PricesBlock = styled.div`
    flex: 1 1 auto;
    display: flex;
    align-items: flex-start;
    min-width: 235px;
    min-height: 36px;
`;

export const TariffPrice = styled.h3`
    ${FONTS.H3};
`;

export const OldPrice = styled.div`
    position: relative;
    margin-left: 8px;

    ${FONTS.XS};
    color: ${COLORS.TextHint};

    &:after {
        position: absolute;
        content: '';
        width: 100%;
        height: 1px;
        left: 0;
        top: 11px;
        background: ${COLORS.GRAY_DARK};
    }
`;

export const ActionLabel = styled(Label)`
    margin: 8px 0;
    border-radius: ${BorderRadius.labelRadius};
`;

export const StyledBaseButton = styled(BaseButton)`
    max-width: unset;
    width: calc(100% + 16px);
    margin: 0 -8px;
`;

export const SubscriptionLabel = styled.div`
    ${FONTS.H5};
    padding: 4px 12px;
    border-radius: ${BorderRadius.OuterBlockRadius};
    background: rgba(${hex2rgb(COLORS.BgMain)}, .2);
    margin-right: 4px;
    white-space: nowrap;
`;
